import { normalizeHexNumber } from '@/utils/color'
import type { Position } from '@/voxelMath'
import { DEFAULT_VOXEL_MATERIAL, type VoxelData } from '@/voxelTypes'
import {
  BLOB_MOLTEN_SILVER_COLOR,
  BLOB_MOLTEN_SILVER_MATERIAL,
} from '@/voxels/blob_sdf/constants'

export type DensityStartSize = {
  id: 'small' | 'medium' | 'large' | 'huge'
  label: string
  voxelsPerCm: number
  /**
   * Fixed size instead of screen density: the shorter base side is exactly this many voxels
   * (the longer side follows the Screen/Square choice, the height the Slab/Mid/Tall ratio).
   */
  minSide?: number
}

export type HomePreviewTheme = {
  backgroundColor: string
  baseColor: string
  guideColor: string
  paletteColors: number[]
  paletteItems: VoxelData[]
  gridThickness?: number
}

export type HomePreviewThemePreset = {
  id: string
  label: string
  theme: HomePreviewTheme
}

export const DENSITY_START_SIZES: readonly DensityStartSize[] = [
  { id: 'small', label: 'Small', voxelsPerCm: 0.5 },
  { id: 'medium', label: 'Medium', voxelsPerCm: 2 },
  { id: 'large', label: 'Large', voxelsPerCm: 2.85 },
  // Large-volume play (docs/large-volume-plan.md): chunked storage and renderers make 128 per side
  // interactive; the swept brush reaches radius 32 here.
  { id: 'huge', label: 'Huge', voxelsPerCm: 5.7, minSide: 128 },
]

export const DEFAULT_CUSTOM_BOUNDS: Position = { x: 64, y: 48, z: 64 }
export const DEFAULT_HOME_PREVIEW_GRID_THICKNESS = 0.1
export const HOME_PREVIEW_FALLBACK_COLOR = 0xffffff

export const DEFAULT_HOME_PREVIEW_THEME: HomePreviewTheme = {
  backgroundColor: '#eef2f5',
  baseColor: '#d6dee6',
  guideColor: '#b2bdc7',
  paletteColors: paletteColors(0xff6f61, 0x2dd4bf, 0xfacc15, 0x38bdf8),
  paletteItems: defaultThemePaletteItems(0xff6f61),
  gridThickness: DEFAULT_HOME_PREVIEW_GRID_THICKNESS,
}

/**
 * Bright, cheerful start themes for a child (owner, 2026-10-15: "more kid friendly color schemes to
 * be visible at first"): saturated but soft colours, a floor that reads as a place (grass, sand,
 * sea, candy, a toy box, a night sky), its grid a gentle step from it, and a toy-box palette.
 */
const KID_THEME_PRESETS: readonly HomePreviewThemePreset[] = [
  {
    id: 'meadow',
    label: 'Meadow',
    theme: {
      backgroundColor: '#bfe8ff',
      baseColor: '#7ccf5a',
      guideColor: '#62b34c',
      paletteColors: paletteColors(0xff4d4d, 0xffd23f, 0x3f8efc, 0xff8fd1),
      paletteItems: kidThemePaletteItems(0xff4d4d, 0x3f8efc, 0xffd23f),
    },
  },
  {
    id: 'toybox',
    label: 'Toy box',
    theme: {
      backgroundColor: '#fff4d6',
      baseColor: '#fbfbfd',
      guideColor: '#d6dae1',
      paletteColors: paletteColors(0xff4d4d, 0xff9f1c, 0xffd23f, 0x2ec4b6, 0x3f8efc, 0x9b5de5),
      paletteItems: kidThemePaletteItems(0xff4d4d, 0x9b5de5, 0x2ec4b6),
    },
  },
  {
    id: 'ocean',
    label: 'Ocean',
    theme: {
      backgroundColor: '#d4f1ff',
      baseColor: '#4cc3ff',
      guideColor: '#39a6e5',
      paletteColors: paletteColors(0xffd23f, 0xff7b54, 0x2ec4b6, 0xffffff),
      paletteItems: kidThemePaletteItems(0xffd23f, 0xff7b54, 0x2ec4b6),
    },
  },
  {
    id: 'candy',
    label: 'Candy',
    theme: {
      backgroundColor: '#fff0f7',
      baseColor: '#ff6fae',
      guideColor: '#e65294',
      paletteColors: paletteColors(0x8f6bff, 0x4fd1ff, 0xffe15c, 0xffffff),
      paletteItems: kidThemePaletteItems(0x8f6bff, 0x4fd1ff, 0xffe15c),
    },
  },
  {
    id: 'sandbox',
    label: 'Sandbox',
    theme: {
      backgroundColor: '#aee6ff',
      baseColor: '#f6d58b',
      guideColor: '#deb66a',
      paletteColors: paletteColors(0xff6b6b, 0x4dabf7, 0x51cf66, 0xffd43b),
      paletteItems: kidThemePaletteItems(0xff6b6b, 0x4dabf7, 0x51cf66),
    },
  },
  {
    id: 'night',
    label: 'Night',
    theme: {
      backgroundColor: '#1b1446',
      baseColor: '#2c2370',
      guideColor: '#41378b',
      paletteColors: paletteColors(0x5cf2c8, 0xffd23f, 0xff6ec7, 0x7ab8ff),
      paletteItems: kidThemePaletteItems(0x5cf2c8, 0xff6ec7, 0xffd23f),
    },
  },
]

/** The theme a fresh child starts with (no theme remembered yet). */
export const DEFAULT_NEW_WORLD_THEME: HomePreviewTheme = KID_THEME_PRESETS[0].theme

export const HOME_PREVIEW_THEME_PRESETS: readonly HomePreviewThemePreset[] = [
  // The kid themes first, so they show without swiping; then the earlier, calmer ones.
  ...KID_THEME_PRESETS,
  {
    id: 'studio',
    label: 'Studio',
    theme: DEFAULT_HOME_PREVIEW_THEME,
  },
  {
    id: 'blueprint',
    label: 'Blueprint',
    theme: {
      backgroundColor: '#081a2f',
      baseColor: '#102b46',
      guideColor: '#1d445f',
      paletteColors: paletteColors(0x6ee7ff, 0xffc857),
      paletteItems: defaultThemePaletteItems(0x6ee7ff),
    },
  },
  {
    id: 'juicy',
    label: 'Juicy',
    theme: {
      backgroundColor: '#a20d58',
      baseColor: '#e93a0f',
      guideColor: '#c02c08',
      paletteColors: paletteColors(0xffd3e5, 0x62f0c5, 0xffe166, 0xff5e5b),
      paletteItems: defaultThemePaletteItems(0xffd3e5),
    },
  },
  {
    id: 'ember',
    label: 'Ember',
    theme: {
      backgroundColor: '#241512',
      baseColor: '#5a2419',
      guideColor: '#793722',
      paletteColors: paletteColors(0xffd6a5, 0xff8a4a),
      paletteItems: defaultThemePaletteItems(0xffd6a5),
    },
  },
  {
    id: 'mint',
    label: 'Mint',
    theme: {
      backgroundColor: '#13eda4',
      baseColor: '#069464',
      guideColor: '#057952',
      paletteColors: paletteColors(0x8dffdc, 0xffe166, 0x62d5ff, 0xb69cff, 0xff7a66, 0xff8ec7),
      paletteItems: defaultThemePaletteItems(0x8dffdc),
    },
  },
  {
    id: 'museum',
    label: 'Museum',
    theme: {
      backgroundColor: '#f8f1e6',
      baseColor: '#e2d3bf',
      guideColor: '#c2b39f',
      paletteColors: paletteColors(0x2d7d82, 0xbf6f45, 0xd5a021, 0x5e83b8, 0x8b6bb1, 0xb85757),
      paletteItems: defaultThemePaletteItems(0x2d7d82),
    },
  },
]

function paletteColors(...colors: number[]): number[] {
  return colors.map(normalizeHexNumber)
}

function defaultThemePaletteItems(currentColor: number): VoxelData[] {
  return [
    { type: 'solid', color: normalizeHexNumber(currentColor) },
    { type: 'blob', color: 0xb8ebff, material: DEFAULT_VOXEL_MATERIAL },
    { type: 'cloud', color: 0xf4fbff },
    { type: 'blob', color: BLOB_MOLTEN_SILVER_COLOR, material: BLOB_MOLTEN_SILVER_MATERIAL },
    { type: 'glass_cube', color: 0x66ccff },
    { type: 'tube', color: 0xffd100 },
    { type: 'sphere', color: 0xff66cc },
    { type: 'googly_eye' },
  ]
}

/** A child's palette: a block, a glossy blob and a ball in the theme's colours, then a cloud, a heart,
 * a tree, a tube and googly eyes. */
function kidThemePaletteItems(first: number, second: number, third: number): VoxelData[] {
  return [
    { type: 'solid', color: normalizeHexNumber(first) },
    { type: 'blob', color: normalizeHexNumber(second), material: DEFAULT_VOXEL_MATERIAL },
    { type: 'sphere', color: normalizeHexNumber(third) },
    { type: 'cloud', color: 0xf4fbff },
    { type: 'heart', color: 0xff5f8f },
    { type: 'tree', color: 0x3fae4a },
    { type: 'tube', color: 0xffd100 },
    { type: 'googly_eye' },
  ]
}
